import { MicOff } from 'lucide-react';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';
import { cn } from '@/lib/cn';

// Mic-health sprint: after a hearing rehearsal where a muted headset produced a
// perfectly healthy — and perfectly empty — transcript, the mic can never fail
// invisibly again. Next to the Listening button the operator gets (1) a device
// picker persisted across reloads and (2) a live input level bar fed by the
// captured stream, so "the app hears me" is visible at a glance.

export function MicControls() {
  const { supported, isListening, engine, micDevices, micDeviceId, setMicDevice, inputLevel, micSilent } =
    useTranscriptionContext();

  if (!supported) {
    return null;
  }

  // Chrome exposes 'default'/'communications' pseudo-devices; our explicit
  // "Default mic" option (empty value) covers that, so list real devices only.
  const realDevices = micDevices.filter(
    (d) => d.deviceId !== 'default' && d.deviceId !== 'communications',
  );

  return (
    <div className="hidden items-center gap-2 sm:flex">
      <select
        value={micDeviceId}
        onChange={(e) => setMicDevice(e.target.value)}
        aria-label="Microphone input device"
        title="Which microphone to capture. Labels appear after the first Start listening."
        className="h-9 max-w-[11rem] cursor-pointer truncate rounded-md border border-stone-700/80 bg-stone-900 px-2 text-xs text-stone-300 focus:outline-none focus:ring-1 focus:ring-amber-700/60"
      >
        <option value="">Default mic</option>
        {realDevices.map((d, i) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label || `Microphone ${i + 1}`}
          </option>
        ))}
      </select>
      <InputLevelBar level={inputLevel} active={isListening && engine === 'deepgram'} silent={micSilent} />
    </div>
  );
}

// Small realtime level bar: green fill while audio is flowing, amber outline
// once the silent-mic detector has tripped, dim when not listening.
function InputLevelBar({
  level,
  active,
  silent,
}: {
  level: number;
  active: boolean;
  silent: boolean;
}) {
  return (
    <div
      role="meter"
      aria-label="Microphone input level"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(level * 100)}
      title={active ? 'Live microphone input level' : 'Input level (shows while listening)'}
      className={cn(
        'h-2 w-14 shrink-0 overflow-hidden rounded-full border',
        silent && active ? 'border-amber-500/80 bg-amber-950/40' : 'border-stone-700/80 bg-stone-900',
      )}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-150',
          active ? 'bg-emerald-500' : 'bg-stone-700',
        )}
        style={{ width: `${Math.round(Math.min(1, active ? level : 0) * 100)}%` }}
      />
    </div>
  );
}

// Full-width banner under the header. Prominent amber when the mic has been
// silent for 10s while listening; a quieter strip for device-fallback notices.
export function MicHealthBanner() {
  const { isListening, engine, micSilent, micNotice } = useTranscriptionContext();
  const showSilent = isListening && engine === 'deepgram' && micSilent;

  if (!showSilent && !micNotice) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-5 pt-3 sm:px-6">
      {showSilent ? (
        <div
          role="alert"
          className="flex items-center gap-3 rounded-md border border-amber-500/70 bg-amber-950/60 px-4 py-3 text-sm font-medium text-amber-200"
        >
          <MicOff className="h-5 w-5 shrink-0 text-amber-400" aria-hidden />
          <span>
            No audio detected from your microphone. Check the mic selector in the header, your
            headset mute button, and Windows sound settings.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-amber-900/40 bg-amber-950/20 px-4 py-2 text-xs text-amber-300/90">
          <MicOff className="h-3.5 w-3.5 shrink-0 text-amber-400" aria-hidden />
          <span>{micNotice}</span>
        </div>
      )}
    </div>
  );
}
